import ElakayImage from '../../components/images/ElakayImage';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  bulkAssignRoleChangeRequests,
  decideRoleChangeRequest,
  getAssignedRoleChangeRequests,
  getRoleChangeReviewers,
  isAuthenticated,
  respondRoleChangeTransfer
} from "../auth";
import { FaCopy, FaEye, FaFilter } from "react-icons/fa";
import Modal from "react-bootstrap/Modal";
import "./AssociateRoleRequests.css";
import { roleDisplayLabel } from "../../utils/roleDisplayLabel";
import { elakayAutoText } from "../pageInside/dataElakay";
import ApplicationLinksList from "../pageInside/applicationLinks/ApplicationLinksList";
import { ROLE_APPLICATION_QUESTIONS } from "../../utils/roleApplicationQuestions";


const STATUS_LABELS = {
  pending: "Pending",
  pending_associate_approval: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  transferred: "Transferred",
  accepted: "Accepted",
  completed: "Completed",
  cancelled: "Cancelled",
};

// Statuses that still need someone's decision — shared by the per-row
// Approve/Reject enablement, the "No decision yet" filter and the
// "Needs action" filter below, so this rule only ever lives in one place.
const UNDECIDED_STATUSES = ["pending_associate_approval", "pending", "accepted"];
const isUndecided = (request) => UNDECIDED_STATUSES.includes(request.status);
const isActionable = (request) => Boolean(request.publicId) && isUndecided(request);
const needsMyAction = (request) =>
  isActionable(request) || request.ownTransfer?.status === "pending";
// Roles the server says this reviewer may grant for this request (the
// requested role first). Older responses without the list: requested only.
const approvableRolesOf = (request) =>
  Array.isArray(request.approvableRoles) ? request.approvableRoles : [request.requestedRole];
const TOKEN_TYPE_LABELS = {
  EVENT_ROLE_TOKEN: ["eventWorldToken", "Event World Token"],
  PROMOTION_TOKEN: ["promotionToken", "Promotion Token"],
  MY_QR_CODE: ["myQrToken", "My QR Code"],
};

const AssociateRoleRequests = ({ infoSource }) => {
  const { token } = isAuthenticated();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reasonById, setReasonById] = useState({});
  const [busyId, setBusyId] = useState("");
  const [busyDecision, setBusyDecision] = useState("");
  const [copiedKey, setCopiedKey] = useState("");
  const decisionLocks = useRef(new Set());
  const selectAllRef = useRef(null);
  const [previewRequest, setPreviewRequest] = useState(null);
  const [approvedRoleById, setApprovedRoleById] = useState({});
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [bulkDecision, setBulkDecision] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [showFiltersModal, setShowFiltersModal] = useState(false);

  const myPublicId = infoSource?.userInfo?.publicId || null;

  const tr = (key, fallback) =>
    infoSource?.laguangeElakay?.("roleChangeRequest", infoSource.languageAPI)?.[key] || fallback;
  const statusLabel = (status) => tr(
    `status${STATUS_LABELS[status] || status}`,
    STATUS_LABELS[status] || status || "—"
  );
  const visibleStatus = (request) => {
    if (
      ["pending", "pending_associate_approval"].includes(request.status) &&
      request.transfers?.length
    ) return "transferred";
    return request.status;
  };

  // Named quick filters — each just a label + predicate over one request.
  // Kept as plain data so the "All statuses" catch-all below can append
  // any raw status found in the data without duplicating this logic.
  const namedFilters = [
    { key: "all", label: tr("filterAll", "All"), predicate: () => true },
    { key: "needsAction", label: tr("filterNeedsAction", "Needs action"), predicate: needsMyAction },
    { key: "noDecision", label: tr("filterNoDecision", "No decision yet"), predicate: isUndecided },
    {
      key: "pending",
      label: tr("filterPending", "Pending"),
      predicate: (request) => ["pending", "pending_associate_approval"].includes(visibleStatus(request)),
    },
    {
      key: "transferred",
      label: tr("filterTransferred", "Transferred"),
      predicate: (request) => visibleStatus(request) === "transferred",
    },
    {
      key: "transferredToMe",
      label: tr("filterTransferredToMe", "Transferred to me"),
      predicate: (request) => Boolean(request.ownTransfer),
    },
    {
      key: "transferredByMe",
      label: tr("filterTransferredByMe", "Transferred by me"),
      predicate: (request) =>
        Boolean(myPublicId) &&
        (request.transfers || []).some((transfer) => transfer.transferredBy?.publicId === myPublicId),
    },
    {
      key: "approved",
      label: tr("filterApproved", "Approved"),
      predicate: (request) => visibleStatus(request) === "approved",
    },
    {
      key: "rejected",
      label: tr("filterRejected", "Rejected"),
      predicate: (request) => visibleStatus(request) === "rejected",
    },
    {
      key: "completed",
      label: tr("filterCompleted", "Completed"),
      predicate: (request) => ["approved", "rejected"].includes(request.status),
    },
    {
      key: "workload",
      label: tr("filterWorking", "Working"),
      predicate: (request) => !["approved", "rejected", "cancelled"].includes(request.status),
    },
  ];
  const namedStatusValues = new Set(["pending", "pending_associate_approval", "approved", "rejected"]);

  // Every other raw status actually present in the data (e.g. "accepted",
  // "cancelled") gets its own filter automatically — covers "all statuses
  // available in the system" without hardcoding the full status list twice.
  const extraStatusFilters = useMemo(() => {
    const seen = new Set();
    const extras = [];
    requests.forEach((request) => {
      if (namedStatusValues.has(request.status) || seen.has(request.status)) return;
      seen.add(request.status);
      extras.push({
        key: `status:${request.status}`,
        label: statusLabel(request.status),
        predicate: (candidate) => candidate.status === request.status,
      });
    });
    return extras;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests]);

  const filterChips = useMemo(
    () => [...namedFilters, ...extraStatusFilters].map((filter) => ({
      ...filter,
      count: requests.filter(filter.predicate).length,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requests, extraStatusFilters, myPublicId, infoSource]
  );

  const activeFilterDef = filterChips.find((filter) => filter.key === activeFilter) || filterChips[0];

  const matchesSearch = (request) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return [
      request.requester?.fullName,
      request.requester?.email,
      request.requestedRole,
      request.publicId,
    ].some((value) => String(value || "").toLowerCase().includes(term));
  };

  const filteredRequests = useMemo(
    () => requests.filter((request) => activeFilterDef.predicate(request) && matchesSearch(request)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requests, activeFilterDef, searchTerm]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getAssignedRoleChangeRequests(token);
      setRequests(data.requests || []);
      const availableIds = new Set(
        (data.requests || []).map((request) => request.publicId).filter(Boolean)
      );
      setSelectedRequestIds((current) =>
        current.filter((publicId) => availableIds.has(publicId))
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getRoleChangeReviewers(token)
      .then((data) => setReviewers(data.reviewers || []))
      .catch((requestError) => setError(requestError.message));
  }, [token]);

  const copyValue = async (key, value) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(""), 1400);
  };

  // Default = the requested role when this reviewer may grant it; never a
  // silently substituted other role.
  const approvedRoleFor = (request) => {
    const choices = approvableRolesOf(request);
    const picked = approvedRoleById[request.publicId];
    if (picked && choices.includes(picked)) return picked;
    return choices.includes(request.requestedRole) ? request.requestedRole : "";
  };

  const decide = async (request, decision, approvedRole) => {
    if (decisionLocks.current.has(request.publicId)) return;
    const reason = (reasonById[request.publicId] || "").trim();
    if (decision === "rejected" && !reason) {
      setError(tr("rejectionRequired", elakayAutoText("h_462ed53efa751f", "A rejection reason is required.")));
      return;
    }
    decisionLocks.current.add(request.publicId);
    setBusyId(request.publicId);
    setBusyDecision(decision);
    setError("");
    setSuccess("");
    try {
      await decideRoleChangeRequest(token, request.publicId, {
        decision,
        reason,
        ...(decision === "approved" && approvedRole ? { approvedRole } : {}),
      });
      await load();
      setPreviewRequest(null);
      setSuccess(decision === "approved" ? tr("roleApproved", "Role Approved") : tr("roleRejected", "Role Rejected"));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      decisionLocks.current.delete(request.publicId);
      setBusyId("");
      setBusyDecision("");
    }
  };

  const toggleRequestSelection = (publicId) => {
    if (!publicId) return;
    setSelectedRequestIds((current) =>
      current.includes(publicId)
        ? current.filter((value) => value !== publicId)
        : [...current, publicId]
    );
  };
  const clearSelection = () => setSelectedRequestIds([]);
  const isRequestSelected = (publicId) => selectedRequestIds.includes(publicId);

  // Based on the *filtered/visible* rows so "select all" only selects what
  // the user can currently see — ids selected under a different filter stay
  // selected (selectedRequestIds itself is never touched by filtering).
  const selectableRequests = filteredRequests.filter(isActionable);
  const allSelected =
    selectableRequests.length > 0 &&
    selectableRequests.every((request) => selectedRequestIds.includes(request.publicId));
  const someSelected =
    selectableRequests.some((request) => selectedRequestIds.includes(request.publicId)) &&
    !allSelected;

  const selectAllEligibleRequests = () => {
    const displayedIds = selectableRequests.map((request) => request.publicId);
    const displayedIdSet = new Set(displayedIds);
    setSelectedRequestIds((current) => {
      const selectionOutsideDisplay = current.filter((id) => !displayedIdSet.has(id));
      return allSelected ? selectionOutsideDisplay : [...selectionOutsideDisplay, ...displayedIds];
    });
  };

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const decideSelected = async () => {
    if (!selectedRequestIds.length || !bulkDecision || bulkBusy) return;
    if (bulkDecision === "rejected" && !bulkReason.trim()) {
      setError(tr("rejectionRequired", elakayAutoText("h_462ed53efa751f", "A rejection reason is required.")));
      return;
    }
    setBulkBusy(true);
    setError("");
    setSuccess("");
    const results = await Promise.allSettled(
      selectedRequestIds.map((publicId) =>
        decideRoleChangeRequest(token, publicId, {
          decision: bulkDecision,
          reason: bulkReason.trim(),
        })
      )
    );
    const failures = results.filter((result) => result.status === "rejected");
    setBulkBusy(false);
    await load();
    if (failures.length) {
      const failedIds = selectedRequestIds.filter(
        (_publicId, index) => results[index]?.status === "rejected"
      );
      setSelectedRequestIds(failedIds);
      setError(
        failures[0].reason?.message ||
        tr("bulkDecisionPartialFailure", elakayAutoText("h_cd2ff91f828648", "{count} request(s) could not be processed."))
          .replace(elakayAutoText("h_45e7ea0dbd5621", "{count}"), failures.length)
      );
      return;
    }
    clearSelection();
    setBulkDecision("");
    setBulkReason("");
    setSuccess(tr("bulkDecisionSuccess", elakayAutoText("h_7248ad781e9a5b", "Selected requests were processed successfully.")));
  };

  const respondToTransfer = async (request, response) => {
    setBusyId(request.publicId);
    setError("");
    try {
      await respondRoleChangeTransfer(token, request.publicId, { response });
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyId("");
    }
  };

  const assignSelected = async () => {
    if (!selectedRequestIds.length || !selectedReviewers.length || assigning) return;
    setAssigning(true);
    setError("");
    setSuccess("");
    try {
      await bulkAssignRoleChangeRequests(token, {
        requestPublicIds: selectedRequestIds,
        reviewerPublicIds: selectedReviewers,
      });
      clearSelection();
      setSelectedReviewers([]);
      await load();
      setSuccess(tr("transferSuccess", elakayAutoText("h_db1aa324f2c5a3", "Selected requests were transferred successfully.")));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setAssigning(false);
    }
  };

  if (loading) return <div className="associate-requests-state">{tr("loadingRequests", "Loading requests…")}</div>;

  const statistics = {
    total: requests.length,
    approved: requests.filter((request) => request.status === "approved").length,
    rejected: requests.filter((request) => request.status === "rejected").length,
    pending: requests.filter((request) => ["pending", "pending_associate_approval"].includes(request.status)).length,
    transferred: requests.filter((request) => request.transfers?.length).length,
    completed: requests.filter((request) => ["approved", "rejected"].includes(request.status)).length,
    workload: requests.filter((request) => !["approved", "rejected", "cancelled"].includes(request.status)).length,
  };

  return (
    <section className="associate-requests">
      <header>
        <div>
          <h2>{tr("requestManagement", "Request Management")}</h2>
          <p>{tr("assignedRequestsHelp", "Review only the requests assigned to you. Approval activates the requested role.")}</p>
        </div>
      </header>
      {error && <div className="alert alert-danger" role="alert" aria-live="assertive">{error}</div>}
      {success && <div className="alert alert-success" role="status">{success}</div>}
      {!requests.length && <div className="associate-requests-state">{tr("noRequests", "No assigned requests.")}</div>}

      {!!requests.length && (
        <div className="associate-request-management">
          <div className="associate-request-stats" aria-label={tr("teamDashboard", "Team dashboard")}>
            {Object.entries(statistics).map(([key, value]) => (
              <div key={key}>
                <strong>{value}</strong>
                <span>{tr(key, key.charAt(0).toUpperCase() + key.slice(1))}</span>
              </div>
            ))}
          </div>

          <div className="associate-request-controls">
          <div className="associate-filters">
            <input
              type="search"
              className="associate-search-input"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={tr("searchPlaceholder", "Search by name, email, role or ID…")}
              aria-label={tr("searchLabel", "Search requests")}
            />
            <button
              type="button"
              className="associate-filters-trigger"
              onClick={() => setShowFiltersModal(true)}
              aria-haspopup="dialog"
            >
              <FaFilter aria-hidden="true" />
              {tr("filtersLabel", "Filter requests")}
              {activeFilter !== "all" && (
                <span className="associate-filters-trigger-badge">{activeFilterDef.label}</span>
              )}
            </button>
            {(activeFilter !== "all" || searchTerm) && (
              <button
                type="button"
                className="associate-filter-clear"
                onClick={() => { setActiveFilter("all"); setSearchTerm(""); }}
              >
                {tr("clearFilters", "Clear filters")}
              </button>
            )}
          </div>
          <div className="associate-results-count" role="status">
            {tr("resultsCount", "{count} of {total} requests")
              .replace("{count}", filteredRequests.length)
              .replace("{total}", requests.length)}
          </div>

          </div>

          <Modal
            show={showFiltersModal}
            onHide={() => setShowFiltersModal(false)}
            centered
            dialogClassName="associate-filters-modal"
          >
            <Modal.Header closeButton>
              <Modal.Title>{tr("filtersLabel", "Filter requests")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="associate-filter-chips" role="group" aria-label={tr("filtersLabel", "Filter requests")}>
                {filterChips.map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    className={`associate-filter-chip${activeFilter === filter.key ? " active" : ""}`}
                    onClick={() => { setActiveFilter(filter.key); setShowFiltersModal(false); }}
                    aria-pressed={activeFilter === filter.key}
                  >
                    {filter.label}
                    <span className="associate-filter-chip-count">{filter.count}</span>
                  </button>
                ))}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm associate-btn-compact"
                onClick={() => { setActiveFilter("all"); setSearchTerm(""); setShowFiltersModal(false); }}
              >
                {tr("clearFilters", "Clear filters")}
              </button>
            </Modal.Footer>
          </Modal>

          <div className="associate-bulk-toolbar">
            <div className="associate-bulk-decisions">
              <button
                type="button"
                className="btn btn-sm btn-success associate-btn-compact"
                disabled={!selectedRequestIds.length || bulkBusy}
                onClick={() => setBulkDecision("approved")}
              >
                {tr("approveSelected", "Approve selected")} ({selectedRequestIds.length})
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger associate-btn-compact"
                disabled={!selectedRequestIds.length || bulkBusy}
                onClick={() => setBulkDecision("rejected")}
              >
                {tr("rejectSelected", "Reject selected")} ({selectedRequestIds.length})
              </button>
            </div>
            <details className="associate-reviewer-picker">
              <summary>{tr("chooseReviewers", "Choose reviewers")} <span>{selectedReviewers.length}</span></summary>
              <div className="associate-reviewer-options">
                {!reviewers.length && <p>{tr("noAgents", "No agents found")}</p>}
                {reviewers.map(reviewer => {
                  const alreadyTransferredCount = selectedRequestIds.filter(publicId =>
                    requests.find(request => request.publicId === publicId)?.transfers?.some(
                      transfer => transfer.recipient?.publicId === reviewer.publicId
                    )
                  ).length;
                  const alreadyTransferredToAll = selectedRequestIds.length > 0 && alreadyTransferredCount === selectedRequestIds.length;
                  return <label key={reviewer.publicId}>
                    <input type="checkbox" className="associate-request-checkbox" checked={selectedReviewers.includes(reviewer.publicId)}
                      disabled={alreadyTransferredToAll} onChange={event => {
                        const checked = event.target.checked;
                        setSelectedReviewers(current => checked ? [...current, reviewer.publicId] : current.filter(id => id !== reviewer.publicId));
                      }} />
                    <span><strong>{reviewer.publicName || reviewer.initials || tr("elakayAgent", "Elakay Agent")}</strong>
                      <small>{roleDisplayLabel(reviewer.role)}{alreadyTransferredCount > 0
                        ? ` · ${tr("alreadyTransferred", "Already Transferred")} (${alreadyTransferredCount}/${selectedRequestIds.length})` : ''}</small>
                    </span>
                  </label>;
                })}
              </div>
            </details>
            <button
              type="button"
              className="btn btn-sm btn-primary associate-btn-compact"
              disabled={!selectedRequestIds.length || !selectedReviewers.length || assigning}
              onClick={assignSelected}
            >
              {assigning
                ? tr("transferring", "Transferring…")
                : `${tr("transferSelected", "Transfer selected")} (${selectedRequestIds.length})`}
            </button>
          </div>
          <div className="associate-review-table-wrap">
            <table className="associate-review-table">
              <caption className="visually-hidden">{tr("assignedRequests", "Assigned role requests")}</caption>
              <thead>
                <tr>
                  <th>
                    <input
                      ref={selectAllRef}
                      className="associate-request-checkbox"
                      type="checkbox"
                      checked={allSelected}
                      disabled={!selectableRequests.length}
                      onChange={selectAllEligibleRequests}
                      aria-label={tr("selectAll", "Select all requests")}
                    />
                  </th>
                  <th>{tr("avatar", "Avatar")}</th>
                  <th>{tr("fullName", "Full Name")}</th>
                  <th>{tr("email", "Email")}</th>
                  <th>{tr("requestedRole", "Requested Role")}</th>
                  <th>{tr("status", "Status")}</th>
                  <th>{tr("transfer", "Transfer")}</th>
                  <th>{tr("preview", "Preview")}</th>
                  <th>{tr("approve", "Approve")}</th>
                  <th>{tr("reject", "Reject")}</th>
                </tr>
              </thead>
              <tbody>
                {!filteredRequests.length && (
                  <tr>
                    <td colSpan={10} className="associate-no-match">
                      {tr("noMatchingRequests", "No requests match your filters.")}
                    </td>
                  </tr>
                )}
                {filteredRequests.map((request) => {
                  const pending = isActionable(request);
                  return (
                    <tr key={request.publicId}>
                      <td data-label={tr("selectRequest", "Select request")}>
                        <input
                          className="associate-request-checkbox"
                          type="checkbox"
                          checked={isRequestSelected(request.publicId)}
                          disabled={!pending}
                          onChange={() => toggleRequestSelection(request.publicId)}
                          aria-label={`${tr("selectRequest", "Select request")} ${request.publicId}`}
                        />
                      </td>
                      <td data-label={tr("avatar", "Avatar")}>
                        <span className="associate-table-avatar">
                          {request.requester.initials}
                          {request.requester.avatar && (
                            <ElakayImage preset="avatar-tiny" src={request.requester.avatar} alt="" width={40} height={40} onError={(event) => { event.currentTarget.hidden = true; }} />
                          )}
                        </span>
                      </td>
                      <td data-label={tr("fullName", "Full Name")}>
                        <span className="associate-table-copy-value">
                          {request.requester.fullName}
                          <button
                            type="button"
                            onClick={() => copyValue(
                              `${request.publicId}-full-name`,
                              request.requester.fullName
                            )}
                            aria-label={tr("copyFullName", "Copy full name")}
                          >
                            <FaCopy />
                          </button>
                          {copiedKey === `${request.publicId}-full-name` && <small>{tr("copied", "Copied!")}</small>}
                        </span>
                      </td>
                      <td data-label={tr("email", "Email")}>
                        <span className="associate-table-email">
                          {request.requester.email}
                          <button type="button" onClick={() => copyValue(`${request.publicId}-email`, request.requester.email)} aria-label={tr("copyEmail", "Copy email address")}><FaCopy /></button>
                          {copiedKey === `${request.publicId}-email` && <small>{tr("copied", "Copied!")}</small>}
                        </span>
                      </td>
                      <td data-label={tr("requestedRole", "Requested Role")}>{roleDisplayLabel(request.requestedRole)}</td>
                      <td data-label={tr("status", "Status")}>
                        <span className={`associate-request-status ${visibleStatus(request)}`}>
                          {statusLabel(visibleStatus(request))}
                        </span>
                      </td>
                      <td data-label={tr("transfer", "Transfer")}>
                        {request.ownTransfer?.status === "pending" ? (
                          <span className="associate-transfer-response">
                            <button type="button" disabled={busyId === request.publicId} onClick={() => respondToTransfer(request, "accepted")}>{tr("accept", "Accept")}</button>
                            <button type="button" disabled={busyId === request.publicId} onClick={() => respondToTransfer(request, "rejected")}>{tr("decline", "Decline")}</button>
                          </span>
                        ) : (
                          <span className={`associate-transfer-state ${request.ownTransfer?.status || ""}`}>
                            {request.ownTransfer?.status
                              ? statusLabel(request.ownTransfer.status)
                              : request.transfers?.length
                                ? statusLabel("transferred")
                                : "—"}
                          </span>
                        )}
                      </td>
                      <td data-label={tr("preview", "Preview")}><button type="button" className="associate-icon-button" onClick={() => setPreviewRequest(request)} aria-label={tr("preview", "Preview")}><FaEye /></button></td>
                      <td data-label={tr("approve", "Approve")}><button type="button" className="btn btn-sm btn-success associate-btn-compact" disabled={!pending || busyId === request.publicId || !approvableRolesOf(request).includes(request.requestedRole)} onClick={() => decide(request, "approved")}>{busyId === request.publicId && busyDecision === "approved" ? tr("approving", "Approving…") : tr("approve", "Approve")}</button></td>
                      <td data-label={tr("reject", "Reject")}><button type="button" className="btn btn-sm btn-outline-danger associate-btn-compact" disabled={!pending || busyId === request.publicId} onClick={() => setPreviewRequest(request)}>{tr("reject", "Reject")}</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal show={!!previewRequest} onHide={() => setPreviewRequest(null)} size="lg" centered scrollable>
        <Modal.Header closeButton><Modal.Title>{tr("requestPreview", "Request Preview")}</Modal.Title></Modal.Header>
        <Modal.Body>
          {previewRequest && (
            <div className="associate-request-details">
              <div className="associate-preview-identity">
                <span className="associate-table-avatar large">
                  {previewRequest.requester.initials}
                  {previewRequest.requester.avatar && (
                    <ElakayImage preset="avatar-tiny" src={previewRequest.requester.avatar} alt="" width={64} height={64} onError={(event) => { event.currentTarget.hidden = true; }} />
                  )}
                </span>
                <div className="associate-request-contact">
                  <strong>
                    {previewRequest.requester.fullName}
                    <button
                      type="button"
                      onClick={() => copyValue(
                        `${previewRequest.publicId}-preview-name`,
                        previewRequest.requester.fullName
                      )}
                      aria-label={tr("copyFullName", "Copy full name")}
                    >
                      <FaCopy />
                    </button>
                    {copiedKey === `${previewRequest.publicId}-preview-name` && <small>{tr("copied", "Copied!")}</small>}
                  </strong>
                  <span>
                    {previewRequest.requester.email}
                    <button type="button" onClick={() => copyValue(`${previewRequest.publicId}-preview-email`, previewRequest.requester.email)} aria-label={tr("copyEmail", "Copy email address")}><FaCopy /></button>
                    {copiedKey === `${previewRequest.publicId}-preview-email` && <small>{tr("copied", "Copied!")}</small>}
                  </span>
                </div>
              </div>
              <section className="associate-request-section">
                <h4>{tr("basicInformation", "Basic Information")}</h4>
                <dl className="associate-request-facts">
                  {previewRequest.requester.phone && <><dt>{tr("phone", "Phone")}</dt><dd>{previewRequest.requester.phone}</dd></>}
                  {previewRequest.location && <><dt>{tr("location", "Location")}</dt><dd>{[previewRequest.location.city, previewRequest.location.state, previewRequest.location.zipCode, previewRequest.location.country].filter((value) => value && value !== "N/A").join(", ") || "—"}</dd></>}
                  {!!previewRequest.categories?.length && <><dt>{tr("productCategories", "Product categories")}</dt><dd>{previewRequest.categories.map((category) => category.label).join(", ")}</dd></>}
                  {!!previewRequest.preferredLanguages?.length && <><dt>{tr("preferredLanguages", "Preferred Languages")}</dt><dd>{previewRequest.preferredLanguages.map((code) => code.toUpperCase()).join(", ")}</dd></>}
                  <dt>{tr("status", "Status")}</dt><dd>{statusLabel(visibleStatus(previewRequest))}</dd>
                </dl>
              </section>
              <section className="associate-request-section">
                <h4>{tr("roles", "Roles")}</h4>
                <dl className="associate-request-facts">
                  <dt>{tr("currentActiveRole", "Current Active Role")}</dt><dd>{roleDisplayLabel(previewRequest.requesterRole || "user")}</dd>
                  <dt>{tr("initialRole", "Initial Role")}</dt><dd>{roleDisplayLabel(previewRequest.currentRole || "user")}</dd>
                  <dt>{tr("requestedRole", "Requested Role")}</dt><dd>{roleDisplayLabel(previewRequest.requestedRole)}</dd>
                  {previewRequest.approvedRole && <><dt>{tr("approvedRole", "Approved Role")}</dt><dd>{roleDisplayLabel(previewRequest.approvedRole)}</dd></>}
                </dl>
              </section>
              {(previewRequest.tokenSource || previewRequest.sourceEvent) && (
                <section className="associate-request-section">
                  <h4>{tr("tokenSource", "Token")}</h4>
                  <dl className="associate-request-facts">
                    {previewRequest.tokenSource?.type && <><dt>{tr("tokenType", "Token Type")}</dt><dd>{tr(...(TOKEN_TYPE_LABELS[previewRequest.tokenSource.type] || ["promotionToken", "Promotion Token"]))}</dd></>}
                    {(previewRequest.tokenSource?.title || previewRequest.sourceEvent?.title) && <><dt>{tr("sourceEvent", "Source Event")}</dt><dd>{previewRequest.tokenSource?.title || previewRequest.sourceEvent.title}</dd></>}
                    <dt>{tr("tokenId", "Token ID")}</dt><dd><code>{previewRequest.tokenSource?.pubId || String(previewRequest.sourceEvent?.pubId || "")}</code></dd>
                    {previewRequest.tokenSource?.creator && <><dt>{tr("tokenCreator", "Token Creator")}</dt><dd>{previewRequest.tokenSource.creator.fullName}{previewRequest.tokenSource.creator.role ? ` (${roleDisplayLabel(previewRequest.tokenSource.creator.role)})` : ""}</dd></>}
                  </dl>
                </section>
              )}
              {previewRequest.requester.biography && (
                <section className="associate-request-biography">
                  <h4>{tr("biography", "Biography")}</h4>
                  <p>{previewRequest.requester.biography}</p>
                </section>
              )}
              <section className="associate-request-answers">
                <h4>{tr("applicationAnswers", "Application Answers")}</h4>
                {[
                  ...ROLE_APPLICATION_QUESTIONS,
                  // Any other stored answer key (older applications) stays visible under its own id.
                  ...Object.keys(previewRequest.applicationAnswers || {})
                    .filter((id) => !ROLE_APPLICATION_QUESTIONS.some((question) => question.id === id) && previewRequest.applicationAnswers[id])
                    .map((id) => ({ id, labelKey: id, fallback: id.replace(/([A-Z])/g, " $1") })),
                ].map((question) => {
                  const answer = String(previewRequest.applicationAnswers?.[question.id] || "").trim();
                  return (
                    <div key={question.id} className={answer ? "" : "is-empty"}>
                      <strong>{tr(question.labelKey, question.fallback)}</strong>
                      <small className="associate-question-id">{tr("questionId", "Question ID")}: <code>{question.id}</code></small>
                      <p>{answer || tr("notProvided", "Not provided")}</p>
                    </div>
                  );
                })}
              </section>
              <ApplicationLinksList links={previewRequest.socialLinks || []} infoSource={infoSource} />
              {!!previewRequest.transfers?.length && (
                <section className="associate-transfer-history">
                  <h4>{tr("transferHistory", "Transfer History")}</h4>
                  {previewRequest.transfers.map((transfer, index) => (
                    <div key={`${transfer.recipient?.publicId || index}-${transfer.sentAt || index}`}>
                      <p><strong>{tr("transferredTo", "Transferred To")}:</strong> {transfer.recipient?.fullName || tr("unknownReviewer", "Unknown reviewer")}</p>
                      {transfer.recipient?.email && <p>{transfer.recipient.email}</p>}
                      <span className={`associate-transfer-state ${transfer.status}`}>{statusLabel(transfer.status)}</span>
                      {transfer.status === "accepted" && <p><strong>{tr("acceptedBy", "Accepted By")}:</strong> {transfer.recipient?.fullName}</p>}
                      {transfer.status === "rejected" && <p><strong>{tr("rejectedBy", "Rejected By")}:</strong> {transfer.recipient?.fullName}</p>}
                      {transfer.status === "completed" && <p><strong>{tr("completedBy", "Completed By")}:</strong> {transfer.recipient?.fullName}</p>}
                      {transfer.transferredBy?.fullName && <small>{tr("transferredBy", "Transferred by")} {transfer.transferredBy.fullName}</small>}
                      {transfer.sentAt && <time><strong>{tr("transferDate", "Transfer Date")}:</strong> {new Date(transfer.sentAt).toLocaleString()}</time>}
                      {transfer.decidedAt && (
                        <time>
                          <strong>{tr("acceptanceDate", "Acceptance Date")}:</strong>{" "}
                          {new Date(transfer.decidedAt).toLocaleString()}
                        </time>
                      )}
                    </div>
                  ))}
                  {previewRequest.completedBy && (
                    <div className="associate-transfer-completion">
                      <p><strong>{tr("completedBy", "Completed By")}:</strong> {previewRequest.completedBy.fullName}</p>
                      <p>{previewRequest.completedBy.email}</p>
                      {previewRequest.decidedAt && <time><strong>{tr("completionDate", "Completion Date")}:</strong> {new Date(previewRequest.decidedAt).toLocaleString()}</time>}
                    </div>
                  )}
                </section>
              )}
              {!!previewRequest.history?.length && (
                <section className="associate-decision-history">
                  <h4>{tr("decisionHistory", "Decision History")}</h4>
                  {previewRequest.history.map((entry, index) => (
                    <div key={`${entry.at || index}-${index}`}>
                      <strong>{entry.actor?.fullName || tr("system", "System")}</strong>
                      <span className={`associate-request-status ${entry.status}`}>{statusLabel(entry.status)}</span>
                      {entry.role && <span className="associate-decision-role">{tr("approvedRole", "Approved Role")}: {roleDisplayLabel(entry.role)}</span>}
                      {entry.reason && <p>{entry.reason}</p>}
                      {entry.at && <time>{new Date(entry.at).toLocaleString()}</time>}
                    </div>
                  ))}
                </section>
              )}
              {isActionable(previewRequest) && (
                <section className="associate-approved-role">
                  <label htmlFor="associate-approved-role">{tr("approvedRole", "Approved Role")}</label>
                  <select
                    id="associate-approved-role"
                    value={approvedRoleFor(previewRequest)}
                    disabled={!approvableRolesOf(previewRequest).length}
                    onChange={(event) => setApprovedRoleById((current) => ({ ...current, [previewRequest.publicId]: event.target.value }))}
                  >
                    {!approvedRoleFor(previewRequest) && <option value="">—</option>}
                    {approvableRolesOf(previewRequest).map((role) => (
                      <option key={role} value={role}>
                        {roleDisplayLabel(role)}{role === previewRequest.requestedRole ? ` — ${tr("requestedRole", "Requested Role")}` : ""}
                      </option>
                    ))}
                  </select>
                  <small>{tr("approvedRoleHelp", "The requested role is selected by default. Only roles you are allowed to grant are listed.")}</small>
                  {!approvedRoleFor(previewRequest) && <p className="alert alert-warning" role="status">{tr("noApprovableRole", "The requester already holds this role or a higher one.")}</p>}
                </section>
              )}
              <textarea
                value={reasonById[previewRequest.publicId] || ""}
                onChange={(event) => setReasonById({ ...reasonById, [previewRequest.publicId]: event.target.value })}
                placeholder={tr("rejectionReason", "Reason for rejection")}
                maxLength={1000}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-danger" disabled={busyId === previewRequest?.publicId} onClick={() => decide(previewRequest, "rejected")}>{busyId === previewRequest?.publicId ? tr("rejecting", "Rejecting…") : tr("reject", "Reject")}</button>
          {(() => {
            const approvedRole = previewRequest ? approvedRoleFor(previewRequest) : "";
            const label = approvedRole && approvedRole !== previewRequest?.requestedRole
              ? tr("approveAsRole", "Approve as {role}").replace("{role}", roleDisplayLabel(approvedRole))
              : tr("approve", "Approve");
            return (
              <button type="button" className="btn btn-success" disabled={busyId === previewRequest?.publicId || !approvedRole} onClick={() => decide(previewRequest, "approved", approvedRole)}>
                {busyId === previewRequest?.publicId ? tr("approving", "Approving…") : label}
              </button>
            );
          })()}
        </Modal.Footer>
      </Modal>
      <Modal show={!!bulkDecision} onHide={() => !bulkBusy && setBulkDecision("")} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {bulkDecision === "approved"
              ? tr("approveSelected", "Approve selected")
              : tr("rejectSelected", "Reject selected")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {tr("selectedRequestCount", "{count} request(s) selected.")
              .replace("{count}", selectedRequestIds.length)}
          </p>
          {bulkDecision === "rejected" && (
            <textarea
              className="associate-bulk-reason"
              value={bulkReason}
              onChange={(event) => setBulkReason(event.target.value)}
              placeholder={tr("rejectionReason", "Reason for rejection")}
              maxLength={1000}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" disabled={bulkBusy} onClick={() => setBulkDecision("")}>{tr("cancel", "Cancel")}</button>
          <button type="button" className={bulkDecision === "approved" ? "btn btn-success" : "btn btn-danger"} disabled={bulkBusy || (bulkDecision === "rejected" && !bulkReason.trim())} onClick={decideSelected}>
            {bulkBusy ? tr("processing", "Processing…") : tr("confirm", "Confirm")}
          </button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default AssociateRoleRequests;
